import type { HttpErrorResponse, HttpEvent, HttpInterceptorFn } from '@angular/common/http';
import { catchError, throwError } from 'rxjs';
import { inject } from '@angular/core';
import { StorageService } from '../auth/storage.service';
import { Router } from '@angular/router';
import { LoadingService } from '../state/loading.service';
import { ToastService } from '../state/toast.service';
import { extractApiErrorMessage } from '../../shared/models/api-error.model';

/**
 * Global error interceptor.
 *
 * Responsibilities:
 *  - Hide the global loading overlay on every error response.
 *  - Surface a human-readable toast for every error.
 *  - On 401, drop the session and redirect to /auth/login.
 *  - On 403/404/500, leave the call site to handle the empty/error state.
 *
 * Note: we deliberately do not also clear the loading state on success
 * here. Success is the common path, and the feature component is
 * responsible for turning the spinner off when its data has actually
 * been wired into the view. This avoids a flash where the overlay
 * disappears before the next request kicks off.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const storage = inject(StorageService);
  const loading = inject(LoadingService);
  const toast = inject(ToastService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      loading.show(false);

      switch (error.status) {
        case 401:
          storage.removeToken();
          toast.show(
            'fail',
            "You Don't Have Permission To Access This Page. Please Login Again."
          );
          router.navigate(['auth/login']);
          break;

        case 403:
          // handle not Authorized case here
          break;

        case 404:
          // handle not found case here
          break;

        case 500:
          // handle server error case here
          break;

        default:
          toast.show('fail', extractApiErrorMessage(error.error));
          break;
      }

      return throwError(() => error);
    })
  );
};
